import React, { Component, ReactNode } from "react";
import { classNames } from "primereact/utils";

/* ------------------------------------------------------------------ */
/*  FormField – replaces Reactstrap FormGroup + Label + Row/Col combos
/*  Usage:
/*    <FormField label="Email" required error="Email is required">
/*      <InputText ... />
/*    </FormField>
/*
/*  For grid layouts use FormGrid:
/*    <FormGrid cols={2}>
/*      <FormField label="First">...</FormField>
/*      <FormField label="Last">...</FormField>
/*    </FormGrid>
/* ------------------------------------------------------------------ */

interface FormFieldProps {
    children?: ReactNode;
    label?: string;
    /** Mark field as required (shows asterisk) */
    required?: boolean;
    /** Error message to display below the input */
    error?: string;
    /** Helper text below the input */
    hint?: string;
    className?: string;
    /** Full width (default true) */
    fullWidth?: boolean;
}

interface FormGridProps {
    children?: ReactNode;
    /** Number of columns: 1 | 2 | 3 | 4 (default 1) */
    cols?: 1 | 2 | 3 | 4;
    className?: string;
}

const colClasses: Record<number, string> = {
    1: "grid-cols-1",
    2: "grid-cols-1 sm:grid-cols-2",
    3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
};

class FormGrid extends Component<FormGridProps> {
    render() {
        const { children, cols = 1, className } = this.props;
        return (
            <div className={classNames("grid gap-x-6 gap-y-5", colClasses[cols], className)}>
                {children}
            </div>
        );
    }
}

class FormField extends Component<FormFieldProps> {
    static Grid = FormGrid;

    render() {
        const { children, label, required, error, hint, className, fullWidth = true } = this.props;
        return (
            <div className={classNames({ "w-full": fullWidth }, className)}>
                {label && (
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                        {label}
                        {required && <span className="text-danger-500 ml-0.5">*</span>}
                    </label>
                )}
                {children}
                {error && (
                    <p className="mt-2 text-sm text-danger-600 dark:text-danger-400 flex items-center gap-1">{error}</p>
                )}
                {!error && hint && (
                    <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">{hint}</p>
                )}
            </div>
        );
    }
}

export default FormField;
export { FormGrid };
